import { createLens, type Getter, type Setter } from '@dhmk/zustand-lens';
import type {
  NodeId,
  NodeInstance,
} from '../../components/graph-editor/types/graph';
import type { Draft } from 'immer';
import type { GraphsSlice } from '.';
import type { LogicBlockIndex } from '@morph-mapper/node-logic';
import { OptionEntry, OptionType, OptionVisibility } from '@morph-mapper/types';
import { type Result, ok, err } from 'neverthrow';

export enum INodeErrorKind {
  NoGraphOverride = 'no_graph_override',
}

export type INodeError = {
  kind: INodeErrorKind;
  message: string;
};

export interface INode {
  get: () => NodeInstance;
  getId: () => NodeId;
  getType: () => LogicBlockIndex;
  getKind: () => LogicBlockIndex;

  getQuickView: () => boolean;
  setQuickView: (_isOpen: boolean) => void;

  getOptions: () => Record<string, OptionEntry<unknown>>;
  setOptions: (options: Record<string, OptionEntry<unknown>>) => void;
  setOptionAsGraph: (
    id: string,
    newGraphId: string
  ) => Result<void, INodeError>;
  setOptionType: (id: string, type: OptionType) => void;
  getOptionType: (id: string) => OptionType;
  getOptionValue: (id: string) => any;
  setOptionValue: (id: string, value: any) => void;
  getOptionVisibility: (id: string) => OptionVisibility;
  setOptionVisibility: (id: string, visibility: OptionVisibility) => void;
}

export const createNodeAPI = (
  _set: Setter<Draft<GraphsSlice>>,
  _get: Getter<Draft<GraphsSlice>>,
  graphId: string,
  nodeId: NodeId
): INode => {
  const [set, get] = createLens(_set, _get, [
    'graphs',
    graphId,
    'nodes',
    nodeId,
  ]) as [Setter<Draft<NodeInstance>>, Getter<Draft<NodeInstance>>];

  return {
    get: () => {
      return get();
    },
    getId: () => {
      return nodeId;
    },
    getType: () => {
      return get().kind;
    },
    getKind: () => {
      return get().kind;
    },

    getQuickView: () => {
      return false;
    },
    setQuickView: () => undefined,

    getOptions: () => {
      return get()?.options ?? {};
    },
    setOptions: (options) => {
      set((s) => {
        s.options = options;
      });
    },

    setOptionAsGraph: (id, newGraphId) => {
      const option = get().options[id];
      if (option.type === OptionType.Graph && option.graphId) {
        return err({
          kind: INodeErrorKind.NoGraphOverride,
          message: `Cannot override existing graph, potential dangling reference: ${newGraphId}.`,
        });
      }

      set((s) => {
        s.options[id] = {
          type: OptionType.Graph,
          graphId: newGraphId,
          visibility: s.options[id].visibility,
        };
      });

      _get().registerGraph(graphId, newGraphId);

      return ok();
    },

    setOptionType: (id, type) => {
      const previous = get().options[id];
      if (previous.type === OptionType.Graph && previous.graphId) {
        _get().delete(previous.graphId);
      }

      set((s) => {
        const option = s.options[id];

        if (option.type === type) {
          return;
        }

        if (option.type === OptionType.Graph) {
          option.graphId = undefined;
        } else {
          option.value = null;
        }

        option.type = type;
      });
    },
    getOptionType: (id) => {
      const option = get().options[id];

      return option.type;
    },
    getOptionValue: (id) => {
      const option = get().options[id];

      if (option.type === OptionType.Graph) {
        return option.graphId;
      }

      return option.value;
    },
    setOptionValue: (id, value) => {
      const previous = get().options[id];
      if (previous.type === OptionType.Graph && previous.graphId) {
        return err({
          kind: INodeErrorKind.NoGraphOverride,
          message: `Cannot override existing graph with value: ${value}.`,
        });
      }

      set((s) => {
        const option = s.options[id];

        if (option.type === OptionType.Graph) {
          option.graphId = value;
        } else {
          option.value = value;
        }
      });
    },
    getOptionVisibility: (id) => {
      const option = get().options[id];

      return option.visibility;
    },
    setOptionVisibility: (id, visibility) => {
      set((s) => {
        const option = s.options[id];
        option.visibility = visibility;
      });
    },
  };
};
